import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { CLAVES_INGRESO } from "@/componentes/ingresos/DialogoIngreso"
import { aplicacionesDe, RepartoCxc, useCxcAbiertas } from "@/componentes/ingresos/RepartoCxc"
import { SelectorPagador } from "@/componentes/ingresos/SelectorPagador"
import { cobraCxc, type Ingreso, type Pagador, type Reparto } from "@/componentes/ingresos/tipos"
import { monto as comoCifra } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

const MOTIVO_MINIMO = 10

function invalidar(clienteQuery: ReturnType<typeof useQueryClient>) {
  for (const clave of CLAVES_INGRESO) clienteQuery.invalidateQueries({ queryKey: [...clave] })
}

/**
 * Ponerle el pagador a un ingreso POR DETERMINAR —`asignar`— o aplicar a sus CxC lo que
 * un cliente dejó de anticipo —`aplicar`—. Es el mismo reparto que al reportar.
 */
export function DialogoPagador({
  ingreso,
  modo,
  onCerrar,
}: {
  ingreso: Ingreso
  modo: "asignar" | "aplicar"
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [pagador, setPagador] = useState<Pagador | null>(
    modo === "aplicar" && ingreso.pagador_id !== null
      ? {
          tipo: "CLIENTE",
          id: ingreso.pagador_id,
          nombre: ingreso.pagador ?? "",
          documento: null,
        }
      : null,
  )
  const [montos, setMontos] = useState<Reparto>({})
  // Lo que queda por repartir: todo al asignar; el anticipo al aplicar.
  const disponible = modo === "aplicar" ? ingreso.resto : ingreso.monto
  const conReparto = pagador?.tipo === "CLIENTE" && cobraCxc(ingreso.divisa)
  const abiertas = useCxcAbiertas(
    conReparto && pagador ? pagador.id : null,
    ingreso.divisa,
    disponible,
    ingreso.fecha_valor,
  )
  const aplicaciones = conReparto ? aplicacionesDe(montos, abiertas.data ?? [], ingreso.divisa) : []
  const listo =
    pagador !== null &&
    aplicaciones !== null &&
    (!conReparto || abiertas.data !== undefined) &&
    (modo === "asignar" || (aplicaciones ?? []).length > 0)

  const guardar = useMutation({
    meta: { exito: modo === "asignar" ? "Pagador asignado" : "Anticipo aplicado" },
    mutationFn: () =>
      modo === "asignar"
        ? api.post<Ingreso>(`/api/v1/ingresos/${ingreso.id}/pagador`, {
            pagador: { tipo: pagador?.tipo, id: pagador?.id },
            aplicaciones,
          })
        : api.post<Ingreso>(`/api/v1/ingresos/${ingreso.id}/aplicar`, { aplicaciones }),
    onSuccess: () => {
      invalidar(clienteQuery)
      onCerrar()
    },
  })

  return (
    <Modal
      abierto
      titulo={modo === "asignar" ? "Asignar pagador" : "Aplicar anticipo"}
      descripcion={`${ingreso.codigo} · ${comoCifra(disponible)} ${ingreso.divisa}${
        modo === "aplicar" ? ` sin aplicar de ${ingreso.pagador}` : ""
      }`}
      onCerrar={onCerrar}
      ancho="lg"
    >
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          if (listo) guardar.mutate()
        }}
      >
        {modo === "asignar" && (
          <SelectorPagador
            valor={pagador}
            porDeterminar={false}
            onCambiar={(p) => {
              setPagador(p)
              setMontos({})
            }}
          />
        )}
        {pagador?.tipo === "CLIENTE" && !cobraCxc(ingreso.divisa) && (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Una CxC es en dólares o en bolívares: este ingreso en {ingreso.divisa} queda entero de
            anticipo.
          </p>
        )}
        {conReparto && (
          <RepartoCxc
            abiertas={abiertas}
            divisa={ingreso.divisa}
            monto={disponible}
            reparto={montos}
            onReparto={setMontos}
          />
        )}
        {guardar.error instanceof ErrorApi && (
          <p className="text-sm" style={{ color: "var(--destructive)" }}>
            {guardar.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" variante="primario" disabled={!listo || guardar.isPending}>
            {guardar.isPending ? "Guardando…" : modo === "asignar" ? "Asignar" : "Aplicar"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Quitar el pagador equivocado: se deshace lo que hizo en su cuenta —sus cobros, su
 * devolución— y el ingreso vuelve a POR DETERMINAR. El dinero sigue en el banco.
 */
export function DialogoQuitarPagador({
  ingreso,
  onCerrar,
}: {
  ingreso: Ingreso
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [motivo, setMotivo] = useState("")
  const quitar = useMutation({
    meta: { exito: "Pagador quitado" },
    mutationFn: () =>
      api.post<Ingreso>(`/api/v1/ingresos/${ingreso.id}/quitar-pagador`, {
        motivo: motivo.trim(),
      }),
    onSuccess: () => {
      invalidar(clienteQuery)
      onCerrar()
    },
  })
  const listo = motivo.trim().length >= MOTIVO_MINIMO
  return (
    <Modal
      abierto
      titulo="Quitar pagador"
      descripcion={`${ingreso.codigo} · ${ingreso.pagador ?? ""}`}
      onCerrar={onCerrar}
    >
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          if (listo) quitar.mutate()
        }}
      >
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Se anulan los cobros y la devolución que hizo en su cuenta, y el ingreso queda por
          determinar. El dinero sigue en el banco.
        </p>
        <label className="block space-y-1 text-sm">
          <span className="font-medium">Motivo</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={300}
            rows={3}
            className="campo"
          />
          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
            Al menos {MOTIVO_MINIMO} caracteres.
          </span>
        </label>
        {quitar.error instanceof ErrorApi && (
          <p className="text-sm" style={{ color: "var(--destructive)" }}>
            {quitar.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" variante="peligro" disabled={!listo || quitar.isPending}>
            {quitar.isPending ? "Quitando…" : "Quitar pagador"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
